"use client";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  format,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  getDay,
  parseISO,
} from "date-fns";
import clsx from "clsx";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CalendarDayModal from "./CalendarDayModal";
import { REST_API_URL } from "../api/config";
import { buildHeaders } from "../api/buildHeaders";
import { getCurrencySymbol } from "../utils/currency/getCurrencySymbol";

const API_BASE_URL = REST_API_URL;

export default function WeekView({
  token,
  selectedDate,
  setSelectedDate,
  setDayEvents,
  onSummaryChange,
  refreshKey,
  userCurrency,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [weekEvents, setWeekEvents] = useState({});
  const [userStatus, setUserStatus] = useState(null);
  const [userCurrencyCode, setUserCurrencyCode] = useState(
    userCurrency || "USD",
  );
  const [showModal, setShowModal] = useState(false);
  const [dayAppointments, setDayAppointments] = useState([]);
  const [clickedDate, setClickedDate] = useState(null);

  const createApiInstance = () => {
    return axios.create({
      baseURL: API_BASE_URL,
      headers: buildHeaders(),
    });
  };

  const fetchUserInfo = async () => {
    try {
      if (!token) return;

      const cachedData = localStorage.getItem("userInfo");
      if (cachedData) {
        const parsedData = JSON.parse(cachedData);
        const cacheTime = localStorage.getItem("userInfoTimestamp");
        const currentTime = new Date().getTime();
        if (cacheTime && currentTime - parseInt(cacheTime) < 3600000) {
          setUserCurrencyCode(parsedData.currency || "USD");
          setUserStatus(
            parsedData?.user?.status || parsedData?.user_status || "individual",
          );
          return parsedData;
        }
      }

      const headers = buildHeaders();

      const response = await fetch(`${API_BASE_URL}/auth/userinfo/`, {
        headers,
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const userData = await response.json();
      const status =
        userData?.user?.status || userData?.user_status || "individual";
      const currency = userData?.currency || "USD";

      setUserStatus(status);
      setUserCurrencyCode(currency);

      localStorage.setItem("userInfo", JSON.stringify(userData));
      localStorage.setItem(
        "userInfoTimestamp",
        new Date().getTime().toString(),
      );

      return userData;
    } catch (err) {
      console.error("⚠️ Failed to fetch userInfo:", err.message);
      setUserStatus("individual");
      setUserCurrencyCode("USD");
    }
  };

  const fetchWeekEvents = async () => {
    if (!token) return;

    setWeekEvents({});
    const api = createApiInstance();

    try {
      const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
      const end = endOfWeek(selectedDate, { weekStartsOn: 1 });
      const weekDays = eachDayOfInterval({ start, end });

      const weekMap = {};
      let totalPrice = 0;
      let weekCurrency = userCurrencyCode || "USD";

      for (const day of weekDays) {
        const dateStr = format(day, "yyyy-MM-dd");
        try {
          const res = await api.post(
            `${API_BASE_URL}/client/appointments/by-date/`,
            { date: dateStr },
          );

          let appointments = [];
          if (Array.isArray(res.data)) {
            appointments = res.data;
          } else if (Array.isArray(res.data?.appointments)) {
            appointments = res.data.appointments;
          } else if (res.data?.dates) {
            appointments = res.data.dates.flatMap(
              (item) => item.appointments || [],
            );
          }

          const realAppointments = appointments.filter(
            (a) =>
              a &&
              a.id &&
              a.status?.toLowerCase() !== "canceled" &&
              a.status?.toLowerCase() !== "breake_time",
          );

          realAppointments.forEach((app) => {
            const price = parseFloat(app.price || 0);
            totalPrice += price;

            if (app.currency && !weekCurrency) {
              weekCurrency = app.currency;
            }
          });

          const displayAppointments = realAppointments
            .slice(0, 3)
            .map((app) => ({
              id: app.id,
              clientName: app.client_name || t("home.fallbackClientName"),
              serviceName: app.service_name || t("home.fallbackServiceName"),
              startTime: app.start_time?.slice(0, 5) || "08:00",
              avatar: app.client_avatar || app.image,
              price: parseFloat(app.price || 0),
              currency: app.currency || userCurrencyCode,
            }));

          weekMap[dateStr] = {
            hasAppointments: realAppointments.length > 0,
            appointmentCount: realAppointments.length,
            appointments: displayAppointments,
            totalPrice: realAppointments.reduce(
              (sum, app) => sum + parseFloat(app.price || 0),
              0,
            ),
            currency: weekCurrency,
          };
        } catch (err) {
          weekMap[dateStr] = {
            hasAppointments: false,
            appointmentCount: 0,
            appointments: [],
            totalPrice: 0,
            currency: userCurrencyCode,
          };
          console.warn(`⚠️ Failed to fetch ${dateStr}:`, err.message);
        }
      }

      setWeekEvents(weekMap);

      const currencySymbol = getCurrencySymbol(weekCurrency);

      if (onSummaryChange) {
        onSummaryChange({
          price: totalPrice,
          currency: weekCurrency,
          currencySymbol: currencySymbol,
        });
      }
    } catch (err) {
      console.error("❌ Week Fetch Error:", err.response?.data || err.message);
      if (err.response?.status === 401) {
        localStorage.clear();
        sessionStorage.clear();
        navigate("/sign-in");
      }
    }
  };

  const fetchDayEvents = async (day) => {
    if (!token) return;

    const api = createApiInstance();
    const dateStr = format(day, "yyyy-MM-dd");

    try {
      const res = await api.post(
        `${API_BASE_URL}/client/appointments/by-date/`,
        { date: dateStr },
      );

      let data = [];
      if (Array.isArray(res.data)) {
        data = res.data;
      } else if (Array.isArray(res.data?.appointments)) {
        data = res.data.appointments;
      }

      const detailedEvents = await Promise.all(
        data
          .filter(
            (item) =>
              item && item.id && item.status?.toLowerCase() !== "canceled",
          )
          .map(async (item) => {
            try {
              if (item.status === "breake_time") return null;

              let details = {};
              if (item.id) {
                try {
                  const detailsRes = await api.get(
                    `${API_BASE_URL}/client/appointment-details/`,
                    {
                      params: { id: item.id },
                    },
                  );
                  details = detailsRes.data || {};
                } catch (detailErr) {
                  console.warn(
                    "Could not fetch appointment details:",
                    detailErr,
                  );
                }
              }

              const masterName =
                item.worker?.[0]?.full_name ||
                details.master_name ||
                details.worker_name ||
                (userStatus === "individual"
                  ? t("home.fallbackMasterYou")
                  : t("home.fallbackMasterGeneric"));

              const appointmentCurrency =
                item.currency || details.currency || userCurrencyCode;
              const appointmentCurrencySymbol =
                getCurrencySymbol(appointmentCurrency);

              return {
                id: item.id,
                name:
                  item.client_name ||
                  details.client_name ||
                  t("home.fallbackClientName"),
                service:
                  item.service_name ||
                  details.service_name ||
                  t("home.fallbackServiceName"),
                service_id: item.service_id || details.service_id || null,
                master: masterName,
                comment:
                  item.service_description ||
                  details.comment ||
                  t("home.fallbackNoComment"),
                price: parseFloat(item.price || details.price || 0),
                servicePrice: parseFloat(
                  item.service_price || details.service_price || 0,
                ),
                serviceId: item.service_id || details.service_id || null,
                date:
                  item.date ||
                  details.date ||
                  item.appointment_date_time?.slice(0, 10) ||
                  dateStr,
                timeStart:
                  item.start_time?.slice(0, 5) ||
                  details.appointment_date_time?.slice(11, 16) ||
                  "08:00",
                timeEnd:
                  item.end_time?.slice(0, 5) ||
                  details.appointment_end_time?.slice(11, 16) ||
                  "09:00",
                avatar:
                  item.image || details.client_avatar || item.client_avatar,
                worker_avatar:
                  item.worker?.[0]?.image_url || details.worker_avatar,
                status:
                  item.status || details.status || t("home.statusUpcoming"),
                currency: appointmentCurrency,
                currencySymbol: appointmentCurrencySymbol,
                added_services: Array.isArray(details.added_services)
                  ? details.added_services
                  : Array.isArray(item.added_services)
                    ? item.added_services
                    : [],
                type: item.type === "other" ? "service" : item.type,
              };
            } catch (err) {
              console.error("Error processing appointment:", err);
              return null;
            }
          }),
      );

      const filteredEvents = detailedEvents.filter((event) => event !== null);

      setDayAppointments(filteredEvents);
      if (setDayEvents) setDayEvents(filteredEvents);
      setSelectedDate(day);
      setClickedDate(day);
      setShowModal(true);
    } catch (err) {
      console.error("❌ Day Fetch Error:", err.response?.data || err.message);
      toast.error(t("home.errorLoadAppointments"));
      if (err.response?.status === 401) {
        localStorage.clear();
        sessionStorage.clear();
        navigate("/sign-in");
      }
    }
  };

  useEffect(() => {
    if (token) {
      if (userCurrency) {
        setUserCurrencyCode(userCurrency);
        fetchWeekEvents();
      } else {
        fetchUserInfo().then(() => {
          fetchWeekEvents();
        });
      }
    }
  }, [selectedDate, token, refreshKey, userCurrency]);

  useEffect(() => {
    if (userCurrency) {
      setUserCurrencyCode(userCurrency);
    }
  }, [userCurrency]);

  const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({
    start,
    end: endOfWeek(selectedDate, { weekStartsOn: 1 }),
  });

  return (
    <div className="relative">
      <motion.div
        key={selectedDate.toString()}
        initial={{ x: 100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: -100, opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="grid grid-cols-7 min-w-[640px] md:min-w-0"
      >
        {weekDays.map((day, idx) => {
          const isSelected = isSameDay(day, selectedDate);
          const dayKey = format(day, "yyyy-MM-dd");
          const event = weekEvents[dayKey];
          const dayOfWeek = getDay(day);
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
          const hasAppointments = event?.hasAppointments;

          return (
            <div
              key={idx}
              onClick={() => fetchDayEvents(day)}
              className={clsx(
                "min-h-[80px] md:h-[130px] px-2 py-2 flex flex-col items-center justify-between outline outline-[1px] outline-[#E5E7EB] cursor-pointer transition-colors duration-200",
                isSelected && "bg-[#1a2a8a] text-white",
                !isSelected && "hover:bg-gray-100 text-gray-800",
                isWeekend &&
                  "bg-[repeating-linear-gradient(117deg,#f5f5f5_0_5px,transparent_5px_10px)]",
              )}
            >
              <span className="mt-1 text-[16px] font-medium">
                {format(day, "d")}
              </span>
              <div className="flex flex-col items-center gap-1 w-full">
                {hasAppointments && (
                  <>
                    <div
                      className={clsx(
                        "w-2.5 h-2.5 rounded-full",
                        isSelected ? "bg-white" : "bg-[#1a2a8a]",
                      )}
                    />
                    {event.appointments
                      ?.slice(0, 2)
                      .map((appointment, appIndex) => (
                        <div
                          key={appIndex}
                          className={clsx(
                            "text-xs px-2 py-1 rounded-full w-full text-center truncate",
                            isSelected
                              ? "bg-white text-[#1a2a8a]"
                              : "bg-[#1a2a8a] text-white",
                          )}
                        >
                          {appointment.startTime}
                        </div>
                      ))}
                    {event.appointmentCount > 2 && (
                      <div
                        className={clsx(
                          "text-xs px-2 py-1 rounded-full",
                          isSelected
                            ? "bg-white text-[#1a2a8a]"
                            : "bg-[#1a2a8a] text-white",
                        )}
                      >
                        +{event.appointmentCount - 2}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </motion.div>

      <CalendarDayModal
        open={showModal}
        onClose={() => setShowModal(false)}
        appointments={dayAppointments}
        date={clickedDate}
        userStatus={userStatus}
        userCurrency={userCurrencyCode}
        onSaveSuccess={() => {
          fetchWeekEvents();
          if (clickedDate) {
            fetchDayEvents(clickedDate);
          }
        }}
      />
    </div>
  );
}
